import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { FlexBox, FlexBoxDirection, Input, Label } from '@ui5/webcomponents-react';
import { SaveResult, SpecificQueryType } from 'components/shared/interfaces/SpecificQueryTypes';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import { useBaseQueryStore } from 'stores/CreateBaseQueryStore';
import { useTrainingQueryStore } from 'stores/CreateTrainingQueryStore';
import { validateString } from 'utils/validator';
import { useStyles } from '../CreateQueryView/CreateQueryView.jss';
import { IssueType, useCreateTrainingMutation } from 'generated/graphql';
import isMatch from 'lodash/isMatch';
import { commonTrainingQueryInitialStateFields } from 'stores/slices/TrainingQuerySlices/CommonTrainingQuerySlice';
import { useCreateQueryFreezeStore } from 'stores/CreateQueryFreezeStore';

import WrappingType from '@ui5/webcomponents/dist/types/WrappingType.js';

export const TrainingQuery = forwardRef<SpecificQueryType>((_, ref) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const store = useTrainingQueryStore();
  const freezeStore = useCreateQueryFreezeStore();
  const baseQueryStore = useBaseQueryStore();
  const {
    deadline,
    details,
    projectManager,
    selectedIssueType,
    selectedProject,
    sourceText,
    targetLanguage,
    targetText,
    attachments
  } = baseQueryStore;

  /** GraphQL Queries/Mutations */
  const [{ fetching }, callCreateQuery] = useCreateTrainingMutation();

  useImperativeHandle(ref, () => ({
    save: () => {
      return onSave();
    },
    cancel: () => {
      store.resetData();
    },
    isValid: () => {
      return validateAllFields();
    },
    containsRelevantChanges: () => {
      return containsRelevantChanges();
    }
  }));

  useEffect(() => {
    store.setFetching(fetching);

    return () => store.setFetching(false);
  }, [fetching]);

  const validateXtmProjectName = () => {
    return validateString(store.xtmProjectName, store.setXtmProjectNameValueState);
  };

  const validateFileName = () => {
    return validateString(store.fileName, store.setFileNameValueState);
  };

  const validateAllFields = () => {
    const isXtmProjectNameValid = validateXtmProjectName();
    const isFileNameValid = validateFileName();
    return isXtmProjectNameValid && isFileNameValid;
  };

  const onSave = async (): Promise<SaveResult> => {
    const result = await callCreateQuery({
      input: {
        deadline: dayjs(deadline).format('YYYY-MM-DD'),
        issueType: selectedIssueType as IssueType,
        project: selectedProject,
        sourceText: sourceText,
        targetLanguage: targetLanguage,
        details: details,
        pmName: projectManager,
        targetText: targetText,
        fileName: store.fileName,
        xtmProjectName: store.xtmProjectName
      },
      files: attachments
    });
    const success = !result.error;
    if (success) {
      store.resetData();
      return { id: result?.data?.query.id };
    }
    return { error: result.error };
  };

  const containsRelevantChanges = () => {
    return !isMatch(store, commonTrainingQueryInitialStateFields);
  };

  const onChangeXtmProjectName = (event: CustomEvent) => {
    const { value } = event.target as HTMLInputElement;
    store.setXtmProjectName(value);
    validateString(value, store.setXtmProjectNameValueState);
  };

  const onChangeFileName = (event: CustomEvent) => {
    const { value } = event.target as HTMLInputElement;
    store.setFileName(value);
    validateString(value, store.setFileNameValueState);
  };

  return (
    <>
      <FlexBox direction={FlexBoxDirection.Column}>
        <div className={classes.div}>
          <Label
            id="xtm-project-label"
            className={classes.label}
            showColon
            required
            wrappingType="None"
          >
            <span data-help-id="create-query-training-xtm-project">{t('xtm-project-label')}</span>
          </Label>
          <Input
            aria-labelledby="xtm-project-label"
            valueState={store.xtmProjectNameValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-xtm-project-name')}
              </Label>
            }
            onChange={onChangeXtmProjectName}
            className={classes.formLineInput}
            value={store.xtmProjectName}
            disabled={freezeStore.xtmProjectName}
          ></Input>
        </div>
        <div className={classes.div}>
          <Label
            id="file-name-label"
            className={classes.label}
            showColon
            required
            wrappingType="None"
          >
            <span data-help-id="create-query-training-file-name">{t('file-name-label')}</span>
          </Label>
          <Input
            aria-labelledby="file-name-label"
            valueState={store.fileNameValueState}
            valueStateMessage={
              <Label wrappingType={WrappingType.Normal}>
                {t('display-value-state-message-file-name')}
              </Label>
            }
            onChange={onChangeFileName}
            className={classes.formLineInput}
            value={store.fileName}
            disabled={freezeStore.fileName}
          ></Input>
        </div>
      </FlexBox>
    </>
  );
});
